/**
 * SystemTray — Sidebar section [6]: footer with system settings.
 *
 * Standard controls:
 *   - Display mode toggle (Desktop / LIM)
 *   - Language selector (always shown)
 *
 * The color-palette switcher was removed: the tool is single-palette (light).
 */

import { BaseSection, type SectionId } from '@/core/BaseSection';
import type { Locale } from '@/bridge/bridge-types';
import { isLimSurface } from '@/core/surface';

type DisplayMode = 'desktop' | 'lim';

export interface SystemTrayOptions {
  supportsThemeToggle?: boolean;
  availableLocales?: Locale[];
  defaultDisplayMode?: DisplayMode;
}

export class SystemTray extends BaseSection {
  readonly sectionId: SectionId = 'tray';
  readonly sectionTitle = 'System Tray';

  private readonly options: SystemTrayOptions;
  private localeSelect!: HTMLSelectElement;
  private currentDisplay: DisplayMode;

  constructor(
    element: HTMLElement,
    context: ConstructorParameters<typeof BaseSection>[1],
    options: SystemTrayOptions = {},
  ) {
    super(element, context);
    // Il display-mode (Testo/Mappa) DEVE seguire la SUPERFICIE al boot: se il
    // tool è aperto su LIM (proiezione in aula) le viste "mappa-first" (mappa
    // autore, tappa, argomento) vanno accese di default — altrimenti restano
    // invisibili perché data-display parte da 'desktop'. Un'opzione esplicita
    // vince comunque; il toggle nel tray resta come override manuale.
    this.options = {
      supportsThemeToggle: options.supportsThemeToggle ?? true,
      availableLocales: options.availableLocales ?? [...context.i18n.getAvailableLocales()],
      defaultDisplayMode: options.defaultDisplayMode ?? (isLimSurface() ? 'lim' : 'desktop'),
    };
    this.currentDisplay = this.options.defaultDisplayMode!;
  }

  protected async onInit(): Promise<void> {
    // Controllo VISTA segmentato — Testo / Mappa (non "Desktop/LIM": sono viste
    // separate, il senso per docente/studente è "leggo il testo" vs "esploro la
    // mappa"). desktop=Testo, lim=Mappa (i valori interni restano per compat).
    this.element.innerHTML = `
      <div class="system-tray">
        <div class="tray-row tray-display-row">
          <button type="button" class="tray-display-toggle" data-display-toggle aria-live="polite" aria-label="Cambia vista Testo/Mappa"></button>
        </div>
        <div class="tray-row">
          <label class="tray-label" for="tray-locale-select"></label>
          <select id="tray-locale-select" class="tray-select">
            ${this.options.availableLocales!.map(
              (l) => `<option value="${l}">${l.toUpperCase()}</option>`,
            ).join('')}
          </select>
        </div>
      </div>
    `;

    this.localeSelect = this.element.querySelector<HTMLSelectElement>('#tray-locale-select')!;

    this.localeSelect.value = this.context.locale;
    this.addEventListener(this.localeSelect, 'change', () => {
      const locale = this.localeSelect.value as Locale;
      this.context.bus.emit<{ locale: Locale }>('locale:change', { locale });
    });

    // Vista Testo/Mappa: UN SOLO pulsante che alterna (come il golden inglese).
    // Mostra la DESTINAZIONE (cosa attivi cliccando).
    document.documentElement.setAttribute('data-display', this.currentDisplay);
    const toggleBtn = this.element.querySelector<HTMLButtonElement>('[data-display-toggle]')!;
    this.addEventListener(toggleBtn, 'click', () => {
      const next: DisplayMode = this.currentDisplay === 'desktop' ? 'lim' : 'desktop';
      this.setDisplay(next);
      this.context.bus.emit('display:change', { mode: next });
    });

    // Listen for bridge-initiated display mode changes
    this.addCleanup(
      this.context.bus.on<{ mode: DisplayMode }>('set_display_mode', ({ mode }) => {
        this.setDisplay(mode);
      }),
    );

    this.render();
  }

  private setDisplay(mode: DisplayMode): void {
    this.currentDisplay = mode;
    document.documentElement.setAttribute('data-display', mode);
    const btn = this.element.querySelector<HTMLButtonElement>('[data-display-toggle]');
    if (btn) {
      // Etichetta = DESTINAZIONE (cosa attivi cliccando): in Testo → "Mappa", in Mappa → "Testo".
      const dest: DisplayMode = mode === 'desktop' ? 'lim' : 'desktop';
      const icon = dest === 'lim' ? '🗺️' : '📄';
      const label = dest === 'lim' ? 'Vista mappa' : 'Vista testo';
      btn.innerHTML = `<span class="tray-display-icon" aria-hidden="true">${icon}</span><span class="tray-display-label">${label}</span>`;
    }
  }

  protected onLocaleChange(): void {
    this.render();
  }

  protected onThemeChange(): void {
    this.render();
  }

  private render(): void {
    // Sincronizza lo stato attivo del segmentato Testo/Mappa.
    this.setDisplay(this.currentDisplay);
    const localeLabel = this.element.querySelector<HTMLElement>('.tray-label');
    if (localeLabel) {
      localeLabel.textContent = this.context.i18n.t('sidebar.settings.language');
    }
  }
}
